import type { CartGetData } from '../../types/cart';
import { formatPrice } from '../../utils/formatPrice';
import { amazonAsinKey } from '../deals/amazonCartDeals';
import { isShowableAmazonPrice } from '../deals/amazonItemFilters';

type RecordPriced = {
  price?: string | number;
  priceAsOf?: number;
  savedPrice?: string;
  savedPriceAsOf?: number;
};

type SearchPriced = RecordPriced & {
  priceSaved?: boolean;
};

type RecordOverlaid = RecordPriced & {
  title?: string;
  image?: string;
  savedTitle?: string;
  savedImage?: string;
};

function hasLapsed(item: RecordPriced, now: number): boolean {
  return (
    item.savedPrice !== undefined &&
    item.priceAsOf != null &&
    !isShowableAmazonPrice(item.priceAsOf, now)
  );
}

/** Must stay in step with applyLapsedRecordPrices in website/js/share.js. */
export function withLapsedRecordPrices<T extends RecordPriced>(items: T[], now: number): T[] {
  if (!items.some((item) => hasLapsed(item, now))) return items;
  return items.map((item) =>
    hasLapsed(item, now)
      ? {
          ...item,
          price: item.savedPrice,
          priceAsOf: item.savedPriceAsOf,
          savedPrice: undefined,
          savedPriceAsOf: undefined,
        }
      : item,
  );
}

function shownPrice(price: unknown): number | null {
  const value = Number(price);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function shownCartData(cart: CartGetData, now: number): CartGetData {
  const items = withLapsedRecordPrices(cart.items, now);
  if (items === cart.items) return cart;
  const total = items.reduce((sum, item) => sum + (shownPrice(item.price) ?? 0) * item.quantity, 0);
  return {
    ...cart,
    items,
    cartTotalPrice: total.toFixed(2),
    formattedCartTotalPrice: formatPrice(total, cart.locale, cart.cartCCY, cart.cartCCYS),
    missingCount: items.filter((item) => !item.title || shownPrice(item.price) === null).length,
  };
}

function searchPriceHasLapsed(item: SearchPriced, now: number): boolean {
  return !item.priceSaved && item.priceAsOf != null && !isShowableAmazonPrice(item.priceAsOf, now);
}

// A Make search price has no saved price behind it, so past the limit it is hidden until a new search or a save.
export function withLapsedSearchPrices<T extends SearchPriced>(items: T[], now: number): T[] {
  if (!items.some((item) => searchPriceHasLapsed(item, now))) return items;
  return items.map((item) =>
    searchPriceHasLapsed(item, now) ? { ...item, price: undefined, priceAsOf: undefined } : item,
  );
}

// The cart's own price for an item: behind a record the server keeps it as savedPrice, otherwise it is the price.
// Keyed in cents: a line saved this session keeps Amazon's unpadded "12.9", which the server returns as "12.90".
function storedPrice(item: RecordPriced): string {
  const stored = String(item.savedPrice ?? item.price);
  const amount = Number.parseFloat(stored);
  return Number.isFinite(amount) ? String(Math.round(amount * 100)) : stored;
}

/** Must stay in step with withReloadedRecordPrices in website/js/share.js, where every row is a saved line. */
export function withReloadedRecordPrices<T extends SearchPriced & { asin?: string }>(
  lines: T[],
  reloadedItems: ReadonlyArray<RecordPriced & { asin?: string }>,
): T[] {
  const reloadedByAsin = new Map<string, RecordPriced[]>();
  reloadedItems.forEach((item) => {
    const asin = amazonAsinKey(item.asin);
    if (asin) reloadedByAsin.set(asin, [...(reloadedByAsin.get(asin) ?? []), item]);
  });
  return lines.map((line) => {
    const sameAsin = line.priceSaved ? reloadedByAsin.get(amazonAsinKey(line.asin)) : undefined;
    const reloaded =
      sameAsin && (sameAsin.find((item) => storedPrice(item) === storedPrice(line)) ?? sameAsin[0]);
    return reloaded
      ? {
          ...line,
          price: reloaded.price,
          priceAsOf: reloaded.priceAsOf,
          savedPrice: reloaded.savedPrice,
          savedPriceAsOf: reloaded.savedPriceAsOf,
        }
      : line;
  });
}

// A re-save or duplicate writes back what the cart holds for a row, never the Amazon record shown in its place.
/** Must stay in step with storedCartItem in website/js/list-actions.js. */
export function storedCartItem<T extends RecordOverlaid>(item: T): T {
  const { savedTitle, savedImage, savedPrice, savedPriceAsOf, ...shown } = item;
  return {
    ...shown,
    title: savedTitle ?? item.title,
    image: savedImage ?? item.image,
    ...(savedPrice === undefined ? {} : { price: savedPrice, priceAsOf: savedPriceAsOf }),
  } as T;
}

// A save writes each saved line's stored values back unchanged, so only a line added since then takes on what it showed.
export function withShownPricesAsSaved<T extends SearchPriced>(items: T[]): T[] {
  return items.map((item) =>
    item.priceSaved
      ? item
      : {
          ...item,
          priceSaved: true,
          savedPrice: String(item.price ?? ''),
          savedPriceAsOf: item.priceAsOf,
        },
  );
}
